import { REVISION } from "three";
import type {
  OpsOrbAnalyserTiming,
  OpsOrbFrame,
  OpsOrbVisualState,
  OpsParticleOrbEngine
} from "./ops-orb-engine.js";
import {
  createKaiParticleOrb,
  type KaiParticleOrbEngine,
  type KaiParticleOrbOptions
} from "./ops-seenstage-orb-engine.js";

function clampUnit(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Thin Business Ops lifecycle adapter around the exact newest SeenStage desktop
 * particle engine. It changes identity color and ownership plumbing only; the
 * donor's breath, point field, glow, amplitude curve and cancellation math stay
 * causal authority. No SeenStage runtime, session, credential or data is used.
 */
export async function createOpsSeenStageParticleOrb(
  canvas: HTMLCanvasElement,
  options: {
    reducedMotion?: boolean;
    onFrame?: (frame: OpsOrbFrame) => void;
    onFatalError?: (error: Error) => void;
    signal?: AbortSignal;
  } = {}
): Promise<OpsParticleOrbEngine> {
  if (options.signal?.aborted) {
    throw new DOMException("Luna orb initialization was cancelled.", "AbortError");
  }
  if (REVISION !== "184") throw new Error("Pinned SeenStage Orb runtime changed.");

  let state: OpsOrbVisualState = "idle";
  let destroyed = false;
  let lastProofAt = 0;
  let frame: OpsOrbFrame = {
    audiblePcm: false,
    bass: 0,
    mid: 0,
    treble: 0,
    rms: 0,
    peak: 0,
    level: 0,
    radius: 1,
    glow: 0.055,
    signal: "idle"
  };
  let donor: KaiParticleOrbEngine;
  try {
    const donorOptions: KaiParticleOrbOptions = {
      // WHY [BOL-ORB-DESKTOP-CANONICAL-001]: the user rejected the old
      // SeenStage mobile branch. The donor makes quality a count/DPR cost only;
      // desktop geometry, amplitude mapping, particles and choreography remain
      // canonical at every viewport. Falsifier: mobile selects different motion.
      quality: window.matchMedia("(max-width: 700px)").matches ? "mobile" : "desktop",
      pixelRatio: window.devicePixelRatio || 1,
      reducedMotion: options.reducedMotion ?? false,
      onAmplitudeFrame: (sample) => {
        if (destroyed) return;
        frame = {
          audiblePcm: sample.audiblePcm,
          bass: sample.bass,
          mid: sample.mid,
          treble: sample.treble,
          rms: sample.rms,
          peak: sample.peak,
          level: sample.amplitudeLevel,
          radius: sample.radiusScale,
          glow: clampUnit(Math.max(sample.coreGlow, sample.lineGlow)),
          signal: sample.audiblePcm ? "pcm" : state === "idle" ? "idle" : "state"
        };
        const now = performance.now();
        if (now - lastProofAt >= 80) {
          lastProofAt = now;
          canvas.dataset.opsOrbLevel = frame.level.toFixed(3);
          canvas.dataset.opsOrbBass = frame.bass.toFixed(3);
          canvas.dataset.opsOrbMid = frame.mid.toFixed(3);
          canvas.dataset.opsOrbTreble = frame.treble.toFixed(3);
          canvas.dataset.opsOrbRadius = frame.radius.toFixed(3);
          canvas.dataset.opsOrbSignal = frame.signal;
          canvas.dataset.opsOrbAudiblePcm = String(frame.audiblePcm);
          canvas.dataset.opsOrbState = state;
        }
        options.onFrame?.(frame);
      }
    };
    if (options.onFatalError) donorOptions.onFatalError = options.onFatalError;
    donor = createKaiParticleOrb(canvas, donorOptions);
  } catch (error) {
    const failure = error instanceof Error ? error : new Error(String(error));
    options.onFatalError?.(failure);
    throw failure;
  }

  canvas.dataset.opsOrbRenderer = "seenstage-desktop-canonical";
  canvas.dataset.opsOrbThreeRevision = REVISION;
  canvas.dataset.opsOrbDonorCommit = "afd21965";

  const abort = () => {
    if (destroyed) return;
    destroyed = true;
    donor.destroy();
  };
  options.signal?.addEventListener("abort", abort, { once: true });

  return {
    setState(nextState) {
      state = nextState;
      donor.setState(nextState);
    },
    setAnalyser(analyser, timing: OpsOrbAnalyserTiming = "output") {
      donor.setAnalyser(analyser, timing);
    },
    pushSpeechPulse() {
      // WHY [BOL-ORB-PCM-ONLY-002]: transcript/boundary timing created the
      // competing spazzy animation. Idle breath is engine-owned; actual input
      // or Aoede PCM is the only speech-motion authority.
      return false;
    },
    setReducedMotion(reduced) {
      // Large scan travel obeys the OS preference inside the shared GPU body;
      // calm radial breath and real PCM response remain functional presence.
      donor.setReducedMotion(reduced);
    },
    setPaused(paused) {
      donor.setPaused(paused);
    },
    resize() {
      donor.resize();
    },
    snapshot() {
      return frame;
    },
    captureFrameDataUrlForProof() {
      return donor.captureFrameDataUrlForProof();
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      options.signal?.removeEventListener("abort", abort);
      donor.destroy();
      delete canvas.dataset.opsOrbRenderer;
    }
  };
}
